import { canv } from "@/canv/global.js";
import { isOnlyCommentWidget } from "@/canv/utils.js";
import { checkIsJespoDumps, getFirstSelected, pasteProccessor } from "@/canvasController/helpers";
import { CommentWidget } from "@/elements/comment/index.js";
import { frameOverlappedManager } from "@/elements/frame/FrameOverlappedManager";
import { isTextWidgetV2 } from "@/elements/text/textPartFactory";
import type { Group } from "@/group";
import type { mediator } from "@/mediator/mediator";
import { toolBase } from "@/tools/toolBase.js";
import { copyManager } from "./CopyManager";
import { fastActionManager } from "./FastActionManager";
import { selectionManager } from "./SelectionManager";
import { transformManager } from "./TransformManager";
import {
    calcCandidate,
    findFirstFrameWidget,
    getSelected,
    isClickOnNonFrameInsideGroup,
    isFrameInStructure,
    isWidgetWithTextOrTextWidgetOnly,
    rotating,
    startDragging,
    startResizing,
    startRotating,
} from "./utils";

export class CursorTool extends toolBase {
    getToolView() {
        return `
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path stroke="currentColor" d="M19.1309 9.1031C20.3571 9.60112 20.2634 11.3681 18.9913 11.7338L12.57 13.5802L9.63686 19.584C9.05533 20.7735 7.2985 20.5587 7.02046 19.2642L4.0313 5.32158C3.97788 5.07376 3.99305 4.81606 4.07516 4.57621C4.15727 4.33636 4.30323 4.12344 4.49732 3.96035C4.69142 3.79726 4.9263 3.69018 5.17671 3.65062C5.42712 3.61107 5.68358 3.64053 5.91849 3.73585L19.1309 9.1031Z" stroke-width="1.5"/>
            </svg>
        `;
    }

    getToolTipName() {
        return "Выделение";
    }

    getToolHotKey() {
        return "V";
    }

    unselect() {
        super.unselect();
        selectionManager.clearSelected();
    }

    // API для mode --------------------------------------------------------------------------------
    saveMode(nextMode = "") {
        if (this.isMode("interaction")) {
            const selected = getFirstSelected();

            if (!selected?.shouldStopInteractionOnModeChange?.(nextMode)) {
                return;
            }

            selected.stopInteraction();
        } else if (this.isMode("fastDragging") || this.isMode("dragging")) {
            if (!canv.actionManager.isClick()) {
                canv.eventManager.openRecord();

                for (let k = 0; k < canv.canvasController.selected.length; k++) {
                    canv.canvasController.selected[k].groupTranslatingStop();
                }

                for (let k = 0; k < canv.canvasController.selected.length; k++) {
                    canv.canvasController.selected[k].saveWidgets("move");
                }

                // Очищаем менеджер после завершения перемещения
                frameOverlappedManager.clearOverlapped();

                canv.eventManager.closeRecord();
            }
        } else if (this.isMode("resize") || this.isMode("sideResize")) {
            canv.eventManager.openRecord();

            for (let k = 0; k < canv.canvasController.selected.length; k++) {
                canv.canvasController.selected[k].groupResizingStop(transformManager.catchPointer);
            }

            for (let k = 0; k < canv.canvasController.selected.length; k++) {
                canv.canvasController.selected[k].saveWidgets("resize");
            }

            canv.eventManager.closeRecord();
        } else if (this.isMode("rotate")) {
            canv.eventManager.openRecord();

            for (let k = 0; k < canv.canvasController.selected.length; k++) {
                canv.canvasController.selected[k].groupRotatingStop();
            }

            for (let k = 0; k < canv.canvasController.selected.length; k++) {
                canv.canvasController.selected[k].saveWidgets("move");
            }

            for (let k = 0; k < canv.canvasController.selected.length; k++) {
                canv.canvasController.selected[k].saveItemWrappers();
            }

            canv.eventManager.closeRecord();
        }
    }

    softResetMode(nextMode = "") {
        this.saveMode(nextMode);
        this.mode = ""; // вызывать turnMode("") нельзя, так как turnMode вызывает softResetMode. Получится рекурсия.

        // Сброс levelLines and Dists
        transformManager.reset();

        // сброс fastAction
        fastActionManager.reset();

        // сброс copying
        copyManager.reset();
    }

    turnMode(newMode: string) {
        // Единственный способ менять mode
        let shouldReopenMenu = false;

        if (
            (this.mode === "interaction" && newMode === "") ||
            (this.mode !== "interaction" &&
                (newMode === "interaction" || this.mode === "interaction-click"))
        ) {
            // если мы меняем mode с interacion (interaction-click) на не interaction (interaction-click) или наоборот, то нужно переоткрыть меню
            // Кроме этого места меню управляет setSelected.
            /*
            1) widgetMenu при сборе options смотрит на cursorTool.mode (не учитывает groupOptions)
               Если выйти из interaction через cursorTool.turnMode(""), то нужно переоткрыть widget menu, чтобы уже учитывать groupOptions

            2) При выставлении isInteraction нужно переоткрыть menu, так как widgetMenu смотрит на cursorTool.mode
            */
            if (!isOnlyCommentWidget()) {
                shouldReopenMenu = true;
            }
        }

        this.softResetMode(newMode);
        this.mode = newMode;

        if (newMode === "selecting") {
            selectionManager.startSelecting();
        } else if (newMode === "resize" || newMode === "sideResize") {
            selectionManager.fixSelected("resize");
            startResizing();
        } else if (newMode === "rotate") {
            selectionManager.fixSelected("move", true);
            startRotating();
        } else if (newMode === "dragging" || newMode === "fastDragging") {
            selectionManager.fixSelected("move");
            startDragging();
        } else if (newMode === "copying") {
            copyManager.startCopying();
        }

        if (shouldReopenMenu) canv.widgetMenu.reopen();
    }

    isMode(mode: string) {
        return this.mode === mode;
    }

    chooseCursor() {
        if (
            this.isMode("resize") ||
            this.isMode("sideResize") ||
            selectionManager.isOverCatchers() ||
            selectionManager.isOverSide()
        ) {
            if (
                transformManager.catchPointer === "upRight" ||
                transformManager.catchPointer === "bottomLeft"
            ) {
                document.body.style.cursor = "nesw-resize";
            } else if (
                transformManager.catchPointer === "upLeft" ||
                transformManager.catchPointer === "bottomRight"
            ) {
                document.body.style.cursor = "nwse-resize";
            } else if (
                transformManager.catchPointer === "sideLeft" ||
                transformManager.catchPointer === "sideRight"
            ) {
                document.body.style.cursor = "ew-resize";
            } else if (
                transformManager.catchPointer === "sideTop" ||
                transformManager.catchPointer === "sideBottom"
            ) {
                document.body.style.cursor = "ns-resize";
            }
        } else if (this.isMode("rotate") || selectionManager.isOverRotater()) {
            const mediator = canv.mediator as mediator | null;

            if (transformManager.catchPointer === "upRight") {
                document.body.style.cursor = `url('${mediator?.Get("rotateCursorTopRight")}'), auto`;
            } else if (transformManager.catchPointer === "bottomRight") {
                document.body.style.cursor = `url('${mediator?.Get("rotateCursorBottomRight")}'), auto`;
            } else if (transformManager.catchPointer === "upLeft") {
                document.body.style.cursor = `url('${mediator?.Get("rotateCursorTopLeft")}'), auto`;
            } else if (transformManager.catchPointer === "bottomLeft") {
                document.body.style.cursor = `url('${mediator?.Get("rotateCursorBottomLeft")}'), auto`;
            }
        } else {
            if (canv.cursorTypeByEntity !== "") {
                document.body.style.cursor = canv.cursorTypeByEntity;
                canv.cursorTypeByEntity = "";
            } else {
                document.body.style.cursor = "";
            }
        }
    }

    isNothingWhenInteraction() {
        // проверяет отключать ли catchers, outline, fastActions

        if (
            this.isMode("interaction") &&
            getFirstSelected().isItemWrapper() &&
            getFirstSelected().onlyChild()?.nothingWhenInteraction
        ) {
            return {
                status: true,
                needLowOutline: getFirstSelected().onlyChild()?.nothingWhenInteractionLowOutline,
            };
        }

        return { status: false };
    }

    TryAction(type: string) {
        const isOnlyCopy = canv.isBoardModeViewCopy();

        if (type === "mousedown") {
            if (selectionManager.isOverCatchers() && !isOnlyCopy) {
                this.turnMode("resize");
                canv.widgetMenu.hide();
            } else if (selectionManager.isOverSide() && !isOnlyCopy) {
                this.turnMode("sideResize");
                canv.widgetMenu.hide();
            } else if (selectionManager.isOverRotater() && !isOnlyCopy) {
                this.turnMode("rotate");
                canv.widgetMenu.hide();
            } else if (this.isMode("interaction") && !isOnlyCopy) {
                getFirstSelected().TryAction("mousedown");
            } else if (
                this.TryInteraction("click") /* && !canv.keyboard.isShift()*/ &&
                !isOnlyCopy
            ) {
                // interaction включаем внутри TryInteraction
            } else if (canv.keyboard.isAlt() && !isOnlyCopy) {
                let selected = canv.canvasController.selected;
                if (!selected.length) {
                    const candidate = calcCandidate();
                    if (!candidate) return;

                    const newSelected = [candidate];
                    selected = newSelected;
                    selectionManager.setSelected(newSelected);
                }

                const isLocked = selected[0].isLocked;
                const isCommentWidget = selected[0].children[0] instanceof CommentWidget;
                if (isLocked || isCommentWidget) return;

                this.turnMode("copying");
            } else if (
                selectionManager.isOverSelected() &&
                !canv.canvasController.selected[0]?.isLocked &&
                !isOnlyCopy
            ) {
                this.turnMode("dragging");
                canv.widgetMenu.hide();
            } else if (
                fastActionManager.tryFastAction() &&
                !canv.canvasController.selected[0]?.isLocked &&
                !isOnlyCopy
            ) {
                this.turnMode("fastAction");
            } else {
                const calcContainer = getSelected();

                let moveCandidate = calcCandidate(calcContainer);
                if (moveCandidate && isFrameInStructure(moveCandidate)) {
                    if (!isClickOnNonFrameInsideGroup(moveCandidate)) {
                        const frameWidget = findFirstFrameWidget(moveCandidate);

                        if (frameWidget) {
                            if (frameWidget.getOverArea() === "body" && !canv.keyboard.isShift()) {
                                this.candidateToSelect = moveCandidate;
                                moveCandidate = null;
                            }
                        }
                    }
                }

                if (moveCandidate == null) {
                    selectionManager.clearSelected();
                    this.turnMode("selecting");
                    canv.widgetMenu.hide();
                } else if (!canv.keyboard.isShift()) {
                    if (moveCandidate.isLocked) {
                        this.turnMode("selecting");
                        this.candidateToSelect = moveCandidate;
                    } else {
                        // тут !isShift, поскольку иначе мы сразу делаем setSelected не учитывая shift. Вообщем setSelected делается только внутри chooseSelected.
                        selectionManager.setSelected([moveCandidate]);
                        canv.widgetMenu.hide();
                        this.turnMode("fastDragging");
                    }
                }
            }
        } else if (type === "mousemove") {
            if (this.isMode("interaction")) {
                canv.canvasController.selected[0].TryAction("mousemove");
                canv.redraw();
                canv.appInterface.linkPopupController.onCanvasPointerLeave();
            } else if (this.isMode("selecting")) {
                selectionManager.selecting();
                canv.appInterface.linkPopupController.onCanvasPointerLeave();
            } else if ((this.isMode("dragging") || this.isMode("fastDragging")) && !isOnlyCopy) {
                transformManager.actionAndMag("translate");
                canv.appInterface.linkPopupController.onCanvasPointerLeave();
            } else if ((this.isMode("resize") || this.isMode("sideResize")) && !isOnlyCopy) {
                transformManager.actionAndMag("resize");
                canv.appInterface.linkPopupController.onCanvasPointerLeave();
            } else if (this.isMode("rotate") && !isOnlyCopy) {
                rotating();
                canv.appInterface.linkPopupController.onCanvasPointerLeave();
            } else if (this.isMode("fastAction") && !isOnlyCopy) {
                fastActionManager.createArrowAndResize();
                canv.appInterface.linkPopupController.onCanvasPointerLeave();
            } else if (this.isMode("copying") && !isOnlyCopy) {
                canv.redraw();
                canv.appInterface.linkPopupController.onCanvasPointerLeave();
            } else {
                const hoveredCandidate = canv.canvasController.hashGrid ? calcCandidate() : null;

                canv.appInterface.linkPopupController.onCanvasPointerMove(
                    hoveredCandidate,
                    canv.actionManager.getActionPoint(),
                );

                canv.redraw();
            }

            // Для draw при наведении на fastAction
            if (fastActionManager.tryFastAction()) {
                canv.redraw();
            }
        } else if (type === "mouseup") {
            const calcContainer = getSelected();

            if (this.isMode("interaction")) {
                getFirstSelected().TryAction("mouseup");
            } else if (this.isMode("selecting")) {
                this.turnMode("");
                canv.widgetMenu.reopen();

                if (this.candidateToSelect) {
                    if (canv.actionManager.isClick()) {
                        selectionManager.setSelected([this.candidateToSelect]);
                    }

                    this.candidateToSelect = null;
                }
            } else if (this.isMode("fastAction") && !isOnlyCopy) {
                this.turnMode("");
            } else if (this.isMode("fastDragging") || this.isMode("dragging")) {
                if (this.isMode("fastDragging")) {
                    // softResetMode вызывается внутри
                    selectionManager.clearSelected();
                } else {
                    this.turnMode("");
                    canv.widgetMenu.reopen();
                }

                if (canv.actionManager.isClick()) {
                    selectionManager.chooseSelected(calcContainer);
                }
            } else if (this.isMode("resize") || this.isMode("sideResize")) {
                if (fastActionManager.fastActionArrow != null) {
                    fastActionManager.emplaceFastActionGroup(
                        fastActionManager.findGroupByFastActionArrow(),
                    );
                } else {
                    this.turnMode("");
                }

                canv.widgetMenu.reopen();
            } else if (this.isMode("rotate")) {
                this.turnMode("");
                canv.widgetMenu.reopen();
            } else if (this.isMode("interaction-click")) {
                this.turnMode("");
            } else if (this.isMode("copying")) {
                if (!copyManager.copyGroupsWithOverlapped.length) {
                    this.turnMode("");
                    return;
                }

                copyManager.stopCopying();
            } else if (this.isMode("")) {
                // Сюда попадаем если с shift кликнем над moveCandidate (fastDragging)
                if (canv.actionManager.isClick()) {
                    selectionManager.chooseSelected(calcContainer);
                }
            }
        } else if (type === "dblclick") {
            if (this.isMode("interaction")) {
                getFirstSelected().TryAction("dblclick");
                return;
            }

            if (canv.canvasController.selected.length === 1) {
                if (canv.canvasController.selected[0].isItemWrapper()) {
                    return;
                }

                for (let i = canv.canvasController.selected[0].children.length - 1; i >= 0; i--) {
                    const child = canv.canvasController.selected[0].children[i] as Group;

                    if (child.isOverAnyInGroup() && !child?.isLocked) {
                        selectionManager.setSelected([
                            canv.canvasController.selected[0].children[i] as Group,
                        ]);
                        break;
                    }
                }
            }
        } else if (type === "tripleclick") {
            if (this.isMode("interaction")) {
                getFirstSelected().TryAction("tripleclick");
                return;
            }
        } else if (type === "keyboardDown") {
            const selectedChild = canv.canvasController.selected[0]?.children?.[0];
            const isTextSelected =
                isTextWidgetV2(selectedChild) || canv.textEditingController.getIsEditing();

            if (canv.contextMenu.isOpened && isTextSelected) {
                canv.contextMenu.hide();
            }

            if (canv.keyboard.isAlt()) return;

            // При редактировании textV2 вставку обрабатывает сам Quill.
            // Иначе получаем двойную вставку: нативный paste + pasteProccessor().
            if (canv.textEditingController.getIsEditing() && canv.keyboard.isPasteCommand()) {
                return;
            }

            if (this.isMode("interaction")) {
                getFirstSelected().TryAction("keyboardDown");
            } else if (canv.keyboard.isSelectAll()) {
                selectionManager.setSelected(canv.canvasController.getFirstLayerGroups());
            } else if (canv.keyboard.isBackspace() || canv.keyboard.isDelete()) {
                if (
                    !canv.canvasController.selected.length ||
                    (isOnlyCommentWidget() && !canv.isBoardModeEditOrOwner())
                )
                    return;

                // Собираем все группы для удаления (включая overlapped фреймов)
                const groupsToDelete = frameOverlappedManager.collectGroupsForDeletion(
                    canv.canvasController.selected,
                );

                canv.eventManager.openRecord();
                for (let i = 0; i < groupsToDelete.length; i++) {
                    canv.canvasController.deleteProcessor(groupsToDelete[i]);
                }
                canv.eventManager.closeRecord();

                selectionManager.clearSelected();
            } else if (canv.keyboard.isGroupCommand()) {
                canv.canvasController.groupProccessor("group");
                return;
            } else if (canv.keyboard.isUnGroupCommand()) {
                canv.canvasController.groupProccessor("ungroup");
            } else if (canv.keyboard.isCutCommand()) {
                canv.canvasController.cutProccessor();
            } else if (canv.keyboard.isCopyCommand()) {
                canv.canvasController.copyProccessor();
            } else if (canv.keyboard.isDuplicateCommand()) {
                canv.canvasController.duplicateProccessor();
            } else if (canv.keyboard.isPasteCommand()) {
                if (isWidgetWithTextOrTextWidgetOnly()) {
                    checkIsJespoDumps().then((isJespoDump) => {
                        if (isJespoDump) {
                            pasteProccessor();
                        } else {
                            this.TryInteractionOnly(canv.canvasController.selected[0], "pastText");
                        }
                    });
                } else {
                    pasteProccessor();
                }
            } else if (canv.keyboard.isToFrontCommand()) {
                canv.canvasController.toProccessor("front");
            } else if (canv.keyboard.isToBackCommand()) {
                canv.canvasController.toProccessor("back");
            }
            // это if условие всегда должно стоять последним, иначе происходит перехват хоткеев
            else if (
                this.isMode("") &&
                isWidgetWithTextOrTextWidgetOnly() &&
                !canv.keyboard.isShift()
            ) {
                this.TryInteractionOnly(canv.canvasController.selected[0], "insertText");
            }
        } else if (type === "touchpadGestureDragging" || type === "touchpadGestureZoom") {
            if (this.isMode("interaction")) {
                getFirstSelected().TryAction(type);
            }
        }
    }

    draw() {
        // Вызывается только тут и это хорошо
        canv.widgetMenu.TryLocateMenu();
        canv.textEditingController.updateOverlayTransform();

        canv.canvasController.updateDims();

        this.chooseCursor();

        if (this.isMode("rotate")) return;

        const nothingWhenInteraction = this.isNothingWhenInteraction();
        if (nothingWhenInteraction.status) {
            if (nothingWhenInteraction.needLowOutline) {
                selectionManager.drawOutline(true);
            }
            return;
        }

        transformManager.drawLevelsAdnDists();

        if (!this.isMode("fastDragging")) {
            selectionManager.drawOutline();
            selectionManager.drawCatchPointer();
            fastActionManager.drawFastAction();
            fastActionManager.drawFastActionShadow();
        }

        if (this.isMode("fastDragging")) {
            selectionManager.drawOutline(false, 1);
        }

        if (this.isMode("selecting")) {
            selectionManager.drawSelecting();
        }

        if (this.isMode("copying")) {
            copyManager.drawCopyingShadow();
        }
    }

    TryInteraction(context: string) {
        for (let i = canv.canvasController.items.length - 1; i >= 0; i--) {
            const entity = canv.canvasController.items[i];

            if (this.TryInteractionOnly(entity, context)) {
                return true;
            }
        }

        return false;
    }

    TryInteractionOnly(entity: Group, context: string) {
        const res = entity.isInteraction(context);

        if (res && res.type !== "none") {
            if (res.type === "interaction") {
                if (res.group) {
                    selectionManager.setSelected([res.group]);
                } else {
                    selectionManager.setSelected([entity]);
                }
                this.turnMode("interaction");
            } else if (res.type === "click") {
                // выставляем этот mode, чтобы после interaction click не срабатывал chooseSelected в mouseup
                this.turnMode("interaction-click");
            }

            return true;
        }

        return false;
    }

    setSelectedAndInteraction(entity: Group) {
        selectionManager.setSelected([entity]);

        const result = getFirstSelected().isInteraction("", true);

        if (result?.type === "interaction") {
            this.turnMode("interaction");
        }
    }

    name = "cursorTool";

    // Mode
    mode = "";

    candidateToSelect: Group | null = null;
}
